import React from 'react';

import { Box, Grid } from '@mui/material';
import _ from 'es-toolkit/compat';

import { validateWithYupSchema } from '@hrlogics/react-form-utils/yup';

import { convertDollarsToCents } from '@clearverify/utils';

import {
  RevenueShareSettingUpdate,
  RevenueShareSettingUpdateMany,
  RevenueShareType,
} from 'src/api';
import { Button } from 'src/components/common';
import { Field, Form, NumberField, ToggleButtonGroupField } from 'src/components/finalForm';
import { REVENUE_SHARE_OPTIONS } from 'src/constants/billing';
import { useNotification } from 'src/hooks/app';
import { useEmployerBillingList } from 'src/hooks/billings/useEmployerBillingList';
import { ChangeEmployerBillingFormFields } from 'src/types/billing';

import { ChangeEmployerBillingFormValues } from './types';
import { getChangeEmployerBillingInitialValues } from './utils';
import { employerBillingRulesFormValidationSchema } from './utils/validation';

export interface EmployerBillingRulesModalFormProps {
  onCloseModal: () => void;
  switchClosability: (closability: boolean) => void;
  billingRuleIds: string | string[] | undefined;
  isAllSelected?: boolean;
  revenueShare?: number;
  revenueShareType: RevenueShareType;
}

export const EmployerBillingRulesModalForm: React.FC<EmployerBillingRulesModalFormProps> = ({
  switchClosability,
  onCloseModal,
  billingRuleIds,
  isAllSelected,
  revenueShare,
  revenueShareType,
}) => {
  const { createApolloContext } = useNotification();
  const { updateEmployerBilling, bulkUpdateEmployerBilling } = useEmployerBillingList();

  const initialValues: ChangeEmployerBillingFormValues = React.useMemo(
    () => getChangeEmployerBillingInitialValues(revenueShare, revenueShareType),
    [revenueShare, revenueShareType],
  );

  const buildDataToSingleUpdate = React.useCallback(
    (formValues: ChangeEmployerBillingFormValues): RevenueShareSettingUpdate => {
      const currentRevenueShareType = formValues[ChangeEmployerBillingFormFields.revenueShareType];

      const data = {
        revenueShareType: currentRevenueShareType,
        revenueShareFlat: 0,
        revenueSharePercents: 0,
      };

      switch (currentRevenueShareType) {
        case RevenueShareType.Flat:
          data.revenueShareFlat = convertDollarsToCents(
            Number(formValues[ChangeEmployerBillingFormFields.revenueShareValue]),
          );
          break;
        case RevenueShareType.Percents:
          data.revenueSharePercents = Number(
            formValues[ChangeEmployerBillingFormFields.revenueShareValue] || 0,
          );
          break;
        default:
          break;
      }

      return data;
    },
    [],
  );

  const buildDataToBulkUpdate = React.useCallback(
    (formValues: ChangeEmployerBillingFormValues): RevenueShareSettingUpdateMany => {
      const currentRevenueShareType = formValues[ChangeEmployerBillingFormFields.revenueShareType];

      const data = {
        revenueShareType: { set: currentRevenueShareType },
        revenueShareFlat: { set: 0 },
        revenueSharePercents: { set: 0 },
      } satisfies RevenueShareSettingUpdateMany;

      if (currentRevenueShareType === RevenueShareType.Flat) {
        data.revenueShareFlat.set = convertDollarsToCents(
          Number(formValues[ChangeEmployerBillingFormFields.revenueShareValue]),
        );
      }

      if (currentRevenueShareType === RevenueShareType.Percents) {
        data.revenueSharePercents.set = Number(
          formValues[ChangeEmployerBillingFormFields.revenueShareValue] || 0,
        );
      }

      return data;
    },
    [],
  );

  const updateSingleEmployerBilling = React.useCallback(
    async (formValues: ChangeEmployerBillingFormValues, id: string) => {
      const data = buildDataToSingleUpdate(formValues);
      await updateEmployerBilling({
        id,
        data,
        context: createApolloContext({
          successMessage: 'Employer billing rule has been successfully updated',
        }),
      });
    },
    [buildDataToSingleUpdate, createApolloContext, updateEmployerBilling],
  );

  const updateMultipleEmployerBilling = React.useCallback(
    async (formValues: ChangeEmployerBillingFormValues, ids: string[]) => {
      const data = buildDataToBulkUpdate(formValues);

      await bulkUpdateEmployerBilling({
        data,
        filter: !isAllSelected
          ? {
              id: {
                in: ids,
              },
            }
          : {},
        context: createApolloContext({
          successMessage: 'Employer billing rules has been successfully updated',
        }),
      });
    },
    [buildDataToBulkUpdate, bulkUpdateEmployerBilling, createApolloContext, isAllSelected],
  );

  const handleSubmit = React.useCallback(
    async (formValues: ChangeEmployerBillingFormValues) => {
      if (!billingRuleIds) return;
      try {
        if (_.isArray(billingRuleIds)) {
          await updateMultipleEmployerBilling(formValues, billingRuleIds);
        } else {
          await updateSingleEmployerBilling(formValues, billingRuleIds);
        }

        switchClosability(true);
        onCloseModal();
      } catch {
        switchClosability(true);
      }
    },
    [
      billingRuleIds,
      switchClosability,
      onCloseModal,
      updateMultipleEmployerBilling,
      updateSingleEmployerBilling,
    ],
  );

  return (
    <Form
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validate={validateWithYupSchema(employerBillingRulesFormValidationSchema)}
    >
      {({ dirty, submitting, valid }) => (
        <React.Fragment>
          <Grid container spacing={2}>
            <Grid size={8}>
              <Field
                fullWidth
                label="Revenue Share"
                name={ChangeEmployerBillingFormFields.revenueShareValue}
                component={NumberField}
                placeholder="0"
              />
            </Grid>
            <Grid size={4}>
              <Field
                name={ChangeEmployerBillingFormFields.revenueShareType}
                component={ToggleButtonGroupField}
                options={REVENUE_SHARE_OPTIONS}
                ToggleButtonProps={{ sx: { width: '56px', height: '56px' } }}
                fullWidth
                exclusive
              />
            </Grid>
          </Grid>

          <Box display="flex" gap={2} mt={4} justifyContent="center">
            <Button variant="outlined" color="error" onClick={onCloseModal} disabled={submitting}>
              Close
            </Button>

            <Button
              type="submit"
              variant="contained"
              disabled={submitting || !dirty || !valid}
              loading={submitting}
            >
              Save Changes
            </Button>
          </Box>
        </React.Fragment>
      )}
    </Form>
  );
};
